import React, { useEffect, useState, useMemo } from "react";
import withRouter from "components/Common/withRouter";
import TableContainer from "components/Common/TableContainer";
import {
  Card, CardBody, Col, Container, Row, Badge, Spinner, Button,
  Modal, ModalHeader, ModalBody, Form, FormGroup, Label, Input
} from "reactstrap";
import Breadcrumbs from "components/Common/Breadcrumb";
import axios from "axios";
import moment from "moment";
import toastr from "toastr";
import "toastr/build/toastr.min.css";

const API_BASE = "https://maasradarapi.stechomeyazilim.info";

const SalaryEntriesList = () => {
  document.title = "Maaş Girişleri | Maaş Radar Admin Paneli";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState({});

  const [detailModal, setDetailModal] = useState(false);
  const [detailItem, setDetailItem] = useState(null);

  const [editModal, setEditModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState({});

  function getAuthHeaders() {
    const token = localStorage.getItem("token");
    return { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" };
  }

  const fetchItems = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/SalaryEntries?$orderby=CreatedAt desc&$top=200`, { headers: getAuthHeaders() });
      setItems(res.data.value || []);
    } catch (error) {
      console.error("Veriler alınamadı", error);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); }, []);

  const showToast = (success, message) => {
    toastr.options = { positionClass: "toast-top-right", timeOut: 5000, closeButton: true, progressBar: true };
    if (success) toastr.success(message, "Başarılı");
    else toastr.error(message, "Hata");
  };

  const handleDelete = (id) => {
    if (!window.confirm("Bu maaş girişini silmek istediğinize emin misiniz?")) return;
    setProcessing(p => ({ ...p, [id + "delete"]: true }));
    axios.patch(`${API_BASE}/SalaryEntries(${id})`, { IsDeleted: true }, { headers: getAuthHeaders() })
      .then(() => { showToast(true, "Maaş girişi silindi."); fetchItems(); })
      .catch((err) => { console.error(err); showToast(false, "Silme sırasında hata oluştu."); })
      .finally(() => setProcessing(p => ({ ...p, [id + "delete"]: false })));
  };

  const openDetail = (item) => { setDetailItem(item); setDetailModal(true); };

  const openEdit = (item) => {
    setEditItem(item);
    setEditForm({
      Profession: item.Profession || "",
      City: item.City || "",
      Salary: item.Salary || 0,
      Experience: item.Experience || "",
      IsDeleted: item.IsDeleted === true,
    });
    setEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!editItem) return;
    setProcessing(p => ({ ...p, [editItem.Id + "save"]: true }));
    try {
      await axios.patch(`${API_BASE}/SalaryEntries(${editItem.Id})`, editForm, { headers: getAuthHeaders() });
      showToast(true, "Maaş girişi güncellendi.");
      setEditModal(false);
      fetchItems();
    } catch (err) {
      console.error(err);
      showToast(false, "Güncelleme sırasında hata oluştu.");
    } finally {
      setProcessing(p => ({ ...p, [editItem.Id + "save"]: false }));
    }
  };

  const columns = useMemo(() => [
    { Header: "ID", accessor: "Id", width: "80px" },
    { Header: "Meslek", accessor: "Profession" },
    { Header: "Şehir", accessor: "City" },
    { Header: "Maaş", accessor: "Salary", Cell: ({ value }) => value ? value.toLocaleString("tr-TR") + " ₺" : "-" },
    { Header: "Tecrübe", accessor: "Experience" },
    { Header: "Durum", accessor: "IsDeleted", Cell: ({ value }) => value ? <Badge color="danger">Silinmiş</Badge> : <Badge color="success">Aktif</Badge> },
    { Header: "Tarih", accessor: "CreatedAt", Cell: ({ value }) => value ? moment(value).format("DD.MM.YYYY") : "-" },
    {
      Header: "İşlemler", accessor: "actions", disableSortBy: true,
      Cell: ({ row }) => (
        <div className="d-flex gap-2">
          <Button size="sm" color="info" onClick={() => openDetail(row.original)}>Detay</Button>
          <Button size="sm" color="primary" onClick={() => openEdit(row.original)}>Düzenle</Button>
          <Button size="sm" color="danger" disabled={processing[row.original.Id + "delete"]} onClick={() => handleDelete(row.original.Id)}>
            {processing[row.original.Id + "delete"] ? <Spinner size="sm" /> : "Sil"}
          </Button>
        </div>
      )
    }
  ], [processing]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Maaş Girişleri" breadcrumbItem="Liste" />
          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  {loading ? (
                    <div className="text-center py-5"><Spinner color="primary" /></div>
                  ) : (
                    <TableContainer columns={columns} data={items} isGlobalFilter={true} isAddOptions={false} customPageSize={10} className="custom-header-css" />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <Modal isOpen={detailModal} toggle={() => setDetailModal(false)} size="lg">
        <ModalHeader toggle={() => setDetailModal(false)}>Maaş Girişi Detayı</ModalHeader>
        <ModalBody>
          {detailItem && (
            <div>
              <p><strong>ID:</strong> {detailItem.Id}</p>
              <p><strong>Meslek:</strong> {detailItem.Profession || "-"}</p>
              <p><strong>Şehir:</strong> {detailItem.City || "-"}</p>
              <p><strong>Maaş:</strong> {detailItem.Salary ? detailItem.Salary.toLocaleString("tr-TR") + " ₺" : "-"}</p>
              <p><strong>Tecrübe:</strong> {detailItem.Experience || "-"}</p>
              <p><strong>Tarih:</strong> {detailItem.CreatedAt ? moment(detailItem.CreatedAt).format("DD.MM.YYYY HH:mm") : "-"}</p>
            </div>
          )}
        </ModalBody>
      </Modal>

      <Modal isOpen={editModal} toggle={() => setEditModal(false)}>
        <ModalHeader toggle={() => setEditModal(false)}>Maaş Girişi Düzenle</ModalHeader>
        <ModalBody>
          {editItem && (
            <Form>
              <FormGroup><Label>Meslek</Label><Input type="text" value={editForm.Profession} onChange={e => setEditForm({...editForm, Profession: e.target.value})} /></FormGroup>
              <FormGroup><Label>Şehir</Label><Input type="text" value={editForm.City} onChange={e => setEditForm({...editForm, City: e.target.value})} /></FormGroup>
              <FormGroup><Label>Maaş</Label><Input type="number" value={editForm.Salary} onChange={e => setEditForm({...editForm, Salary: parseInt(e.target.value) || 0})} /></FormGroup>
              <FormGroup><Label>Tecrübe</Label><Input type="text" value={editForm.Experience} onChange={e => setEditForm({...editForm, Experience: e.target.value})} /></FormGroup>
              <FormGroup check className="mb-3"><Label check><Input type="checkbox" checked={editForm.IsDeleted} onChange={e => setEditForm({...editForm, IsDeleted: e.target.checked})} /> Silinmiş</Label></FormGroup>
              <Button color="primary" disabled={processing[editItem.Id + "save"]} onClick={handleSaveEdit}>
                {processing[editItem.Id + "save"] ? <Spinner size="sm" /> : "Kaydet"}
              </Button>
            </Form>
          )}
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default withRouter(SalaryEntriesList);
